import { useNavigate } from 'react-router-dom';
import { HiBriefcase } from 'react-icons/hi2';
import { formatCurrency, formatPercent, getPnlColor } from '../../utils/formatting';
import { PersonaBadge } from '../workspace/PersonaBadge';
import { Sparkline } from '../shared/Sparkline';

/**
 * HL Hero — active state.
 * Shown when user has a live HYPERLIQUID mainnet portfolio.
 * Per wf-v3.jsx HLHeroActive.
 */
export function HLHeroActive({ portfolio, detail, needsUpgrade = false, onOpenWorkspace }) {
  const navigate = useNavigate();

  const totalValue = detail?.total_value_usd ?? null;
  // Use the platform-canonical 24h PnL (account-value delta inc. unrealized + funding)
  // so this card matches the value shown on the Hyperliquid dashboard widget.
  const pnlUsd = detail?.total_pnl_usd ?? null;
  const pnlPct = detail?.total_pnl_pct ?? null;
  const openPosCount = detail?.open_positions?.length ?? 0;
  const maxPositions = detail?.max_positions ?? '--';
  const cashBalance = detail?.cash_balance_usd ?? null;
  const agent = portfolio?.active_agent;

  // Build sparkline data from timeseries if available
  const sparkData = detail?.timeseries?.map(d => d.equity) || [];

  return (
    <div className="border border-slate-700 rounded-lg p-5 sm:p-6 bg-slate-800/50 border-l-4 border-l-green-500">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs px-2 py-0.5 rounded-full bg-green-900/50 text-green-400 border border-green-700/50 font-semibold">
            LIVE &middot; HL
          </span>
          <h2 className="text-xl sm:text-2xl font-bold text-white">{portfolio.name}</h2>
          <span className="text-xs text-slate-500 hidden sm:inline">your Hyperliquid portfolio</span>
          {needsUpgrade && (
            <span className="text-xs px-2 py-0.5 rounded bg-yellow-900/40 text-yellow-300 border border-yellow-700/50">
              &#9888; trading paused &middot; upgrade to resume
            </span>
          )}
        </div>
        <button
          onClick={onOpenWorkspace}
          className="flex items-center gap-2 px-3 py-1.5 bg-cyan-900/30 hover:bg-cyan-900/50 text-cyan-400 border border-cyan-700/50 rounded text-xs font-semibold transition-colors flex-shrink-0"
        >
          <HiBriefcase className="w-4 h-4" />
          Open Workspace
        </button>
      </div>

      {/* Content row */}
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
        {/* Value + PnL */}
        <div className="flex flex-col gap-1 flex-shrink-0">
          <span className="text-xs text-slate-500 uppercase tracking-wide">Total</span>
          {totalValue !== null ? (
            <span className="text-3xl font-bold text-white leading-none">{formatCurrency(totalValue)}</span>
          ) : (
            <div className="h-9 w-32 bg-slate-700 rounded animate-pulse" />
          )}
          {pnlUsd !== null ? (
            <span className={`text-xs ${getPnlColor(pnlUsd)}`}>
              {pnlUsd >= 0 ? '+' : ''}{formatCurrency(pnlUsd)} ({formatPercent(pnlPct)}) 24h
            </span>
          ) : (
            <div className="h-4 w-24 bg-slate-700 rounded animate-pulse mt-1" />
          )}
        </div>

        {/* Equity curve */}
        <div className="flex-1 hidden sm:flex items-center min-w-0">
          {sparkData.length > 1 ? (
            <Sparkline
              data={sparkData}
              width={300}
              height={80}
              negative={pnlUsd < 0}
              fill
            />
          ) : (
            <div className="w-full h-20 bg-slate-700/30 rounded flex items-center justify-center">
              <span className="text-xs text-slate-500">No chart data</span>
            </div>
          )}
        </div>

        {/* Stats column */}
        <div className="flex flex-col gap-2 flex-shrink-0 min-w-[160px]">
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Open pos</span>
            <span className="font-bold text-white">{openPosCount}/{maxPositions}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Cash</span>
            <span className="font-mono text-white">
              {cashBalance !== null ? formatCurrency(cashBalance) : '--'}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Open recs</span>
            <span className="font-bold text-white">--</span>
          </div>
          <hr className="border-slate-700 border-dashed" />
          {agent && (
            <PersonaBadge name={agent.name} avatarSeed={agent.avatar_seed} size="sm" />
          )}
        </div>
      </div>
    </div>
  );
}
